import React, { useEffect, useId, useRef, useState } from 'react'
import { Loader2, ShieldCheck, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useBrand } from '@/hooks/useBrand'
import {
  mountPaymentElement,
  confirmSetupIntent,
  confirmPaymentIntent,
  type StripeInstance,
  type ConfirmOutcome,
} from '@/lib/stripe-client'

// StripePaymentElement (FE1) — the in-app card-capture surface. Mounts Stripe's PaymentElement
// from a SetupIntent client_secret (created by CreateSetupIntentMutation / RAIL1) and confirms
// the card off-session, or — when `mode="payment"` — confirms an in-app PaymentIntent for the
// claim first charge. SCA / 3D Secure is surfaced via `onRequiresAction` (the backend owns the
// off-session re-auth path); card errors render inline and call `onError`.
//
// This component does ZERO money math. It never receives an `_amount`, never formats currency,
// never rounds — the amount lives entirely on the backend (CreateInvoice/ChargeAccount). All it
// orchestrates is mount → confirm → callback. (R-FE-MATH.)

type CaptureMode = 'setup' | 'payment'

interface StripePaymentElementProps {
  // SetupIntent (mode 'setup') or PaymentIntent (mode 'payment') client_secret from the backend.
  clientSecret: string
  // Per-env Stripe publishable key, injected from the backend (never inlined — R-FE-LIB).
  publishableKey: string
  // 'setup' tokenizes a card for off-session reuse; 'payment' confirms an immediate charge.
  mode?: CaptureMode
  // Label for the confirm button; defaults follow the mode.
  submitLabel?: string
  /**
   * Card saved / payment confirmed. Both ids Stripe resolved are handed back: the PaymentMethod,
   * and — since billing-payers §11.1 — the INTENT that confirmed it.
   *
   * The intent id is what closes the silent-card bug. Vaulting used to be webhook-only, so the
   * caller could only reload and hope it had landed; posting this id lets the server verify and
   * vault the card inside one request. It is a claim, never a credential: the server re-reads the
   * intent from Stripe and trusts nothing the browser says about it.
   */
  onSuccess: (paymentMethodId?: string, intentId?: string) => void
  /**
   * Whether a confirm is in flight — true from the moment the operator presses the button until
   * Stripe resolves, which SPANS the 3-D Secure challenge. The caller uses it to refuse to close
   * the dialog underneath a challenge the operator is still completing.
   */
  onProcessingChange?: (processing: boolean) => void
  // SCA / 3D Secure challenge that couldn't complete in-app — hand off to the re-auth flow.
  onRequiresAction?: () => void
  // A card/validation error message (already surfaced inline; mirrored to the caller).
  onError?: (message: string) => void
}

type MountState = 'loading' | 'ready' | 'failed'

export default function StripePaymentElement({
  clientSecret,
  publishableKey,
  mode = 'setup',
  submitLabel,
  onSuccess,
  onProcessingChange,
  onRequiresAction,
  onError,
}: StripePaymentElementProps) {
  const brand = useBrand()
  // A unique element id per instance so two PaymentElements on one page never collide.
  const containerId = `stripe-payment-element-${useId().replace(/[:]/g, '')}`
  const stripeRef = useRef<StripeInstance | null>(null)
  const elementsRef = useRef<unknown>(null)
  const [mountState, setMountState] = useState<MountState>('loading')
  const [submitting, setSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setMountState('loading')
    mountPaymentElement(`#${containerId}`, clientSecret, publishableKey)
      .then(({ stripe, elements }) => {
        if (cancelled) return
        stripeRef.current = stripe
        elementsRef.current = elements
        setMountState('ready')
      })
      .catch(() => {
        if (cancelled) return
        setMountState('failed')
      })
    return () => {
      cancelled = true
    }
    // clientSecret/publishableKey are stable for a given capture session; re-mount if they change.
  }, [clientSecret, publishableKey, containerId])

  async function handleConfirm() {
    if (!stripeRef.current || !elementsRef.current || submitting) return
    setSubmitting(true)
    onProcessingChange?.(true)
    setErrorMessage(null)
    try {
      const confirm = mode === 'payment' ? confirmPaymentIntent : confirmSetupIntent
      const outcome: ConfirmOutcome = await confirm(
        stripeRef.current,
        elementsRef.current as Parameters<typeof confirmSetupIntent>[1]
      )
      if (outcome.status === 'requires_action') {
        onRequiresAction?.()
        return
      }
      if (outcome.status === 'error') {
        const message = outcome.error?.message ?? 'We couldn’t process that card. Please try again.'
        setErrorMessage(message)
        onError?.(message)
        return
      }
      onSuccess(outcome.paymentMethodId, outcome.intentId)
    } catch {
      const message = 'Something went wrong confirming your card. Please try again.'
      setErrorMessage(message)
      onError?.(message)
    } finally {
      setSubmitting(false)
      onProcessingChange?.(false)
    }
  }

  const defaultLabel = mode === 'payment' ? 'Continue payment' : 'Save card'
  const buttonLabel = submitLabel ?? defaultLabel

  if (mountState === 'failed') {
    return (
      <div className="rounded-xl border border-rose-200 bg-rose-50/60 p-5 text-center shadow-sm">
        <AlertCircle className="mx-auto h-7 w-7 text-rose-500" />
        <p className="mt-3 text-sm font-medium text-foreground">
          We couldn’t load the secure payment form
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Check your connection and refresh the page to try again.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border/60 bg-white p-5 shadow-sm">
        {mountState === 'loading' && (
          <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading secure payment form…
          </div>
        )}
        {/* Stripe mounts the PaymentElement into this node. Kept in the DOM during 'loading'
            (visually hidden) so the mount target exists when Stripe.js resolves. */}
        <div id={containerId} className={mountState === 'ready' ? '' : 'hidden'} />
      </div>

      {errorMessage && (
        <div className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{errorMessage}</span>
        </div>
      )}

      <Button
        onClick={handleConfirm}
        disabled={mountState !== 'ready' || submitting}
        className="w-full rounded-lg"
      >
        {submitting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Processing…
          </>
        ) : (
          <>
            <ShieldCheck className="mr-2 h-4 w-4" />
            {buttonLabel}
          </>
        )}
      </Button>

      <p className="text-center text-xs text-muted-foreground">
        By saving your card you authorise {brand} to charge it for your subscription in line with
        your agreement. Your card is encrypted and tokenised by Stripe. We never see your full card
        number.
      </p>
    </div>
  )
}
